/**
 * מחיר סופי ללקוח בעמוד הצעה ציבורית (טיול מאורגן).
 * חדרי ההצעה נשמרים עם fare אחרי HR (PWC/PWCC) — זה SSOT התצוגה.
 * final_price_ils ישן עלול להיות נטו ספק / baseline שגוי; לא לדרוס את מחיר החדרים.
 *
 * מטבע משני (EUR/USD): נשמר ב-snapshot מ-API (fareDetails / pricingSnapshot),
 * או נגזר מ-ILS ÷ שער חי כשחסר ב-snapshot ישן.
 */

import {
  formatOrganizedTourSecondaryCurrencyDisplay,
} from "./resolveOrganizedTourDualPrice";

function toPositiveIls(v) {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? Math.round(n) : null;
}

function toPositiveFinite(v) {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function normCur(c) {
  if (c == null || c === "") return "";
  return String(c).trim().toUpperCase();
}

/**
 * @param {Array<{ price_ils?: number|null }>|null|undefined} rooms
 * @returns {number|null}
 */
export function sumOrganizedTourPublicOfferRoomsIls(rooms) {
  if (!Array.isArray(rooms) || rooms.length === 0) return null;
  let sum = 0;
  let counted = 0;
  for (const row of rooms) {
    const p = toPositiveIls(row?.price_ils);
    if (p == null) continue;
    sum += p;
    counted += 1;
  }
  if (counted === 0) return null;
  return sum;
}

/**
 * @param {object|null|undefined} vm — public_view_model
 * @returns {{ priceIls: number|null, source: 'rooms_sum'|'final_price_ils'|'none' }}
 */
export function resolveOrganizedTourPublicOfferFinalPriceIls(vm) {
  const root = vm && typeof vm === "object" ? vm : {};
  const fromRooms = sumOrganizedTourPublicOfferRoomsIls(root.rooms);
  const fromFinal = toPositiveIls(root.final_price_ils);
  if (fromRooms != null && fromFinal != null && fromFinal > fromRooms) {
    return { priceIls: fromFinal, source: "final_price_ils" };
  }
  if (fromRooms != null) {
    return { priceIls: fromRooms, source: "rooms_sum" };
  }
  if (fromFinal != null) {
    return { priceIls: fromFinal, source: "final_price_ils" };
  }
  return { priceIls: null, source: "none" };
}

/**
 * רמז מטבע להצעות ישנות בלי primary_currency ב-snapshot.
 * @param {object|null|undefined} vm
 * @returns {'EUR'|'USD'|null}
 */
export function inferOrganizedTourPublicOfferCurrencyHint(vm) {
  const root = vm && typeof vm === "object" ? vm : {};
  const blob = `${root.route_line_he || ""} ${root.tour_title_he || ""}`;
  if (/אמריק|ארה.?ב|USA|קנדה|מקסיק|ניו.?יורק|קליפורנ/i.test(blob)) return "USD";
  if (
    /איטלי|יוון|ספרד|צרפת|גרמני|אוסטרי|שווי|צכ|פולין|קרואט|אלבנ|אירופ|ורונ|מילאנ|רומא|פורטוג|הולנד|בלגי|טורקי|קפריס|בולגר|רומנ|הונגר|אייסלנד|נורווג|שבדי|דנמרק|פינלנד|מולדוב/i.test(
      blob
    )
  ) {
    return "EUR";
  }
  return null;
}

function rateFromLive(liveRates, currency) {
  const cur = normCur(currency);
  if (!cur || !liveRates || typeof liveRates !== "object") return null;
  return (
    toPositiveFinite(liveRates[cur]) ??
    toPositiveFinite(liveRates[String(cur).toLowerCase()]) ??
    null
  );
}

/**
 * סכום במטבע מקור (EUR/USD) ליד מחיר ₪.
 *
 * @param {object|null|undefined} vm
 * @param {number|null|undefined} ilsAmount — סכום ₪ שמוצג
 * @param {{ USD?: number, EUR?: number, GBP?: number }|null|undefined} [liveRates]
 * @param {{ price_primary?: number|null, currency?: string|null }|null|undefined} [roomRow]
 * @returns {{ amount: number|null, currency: string|null, display: string|null, source: string }}
 */
export function resolveOrganizedTourPublicOfferPrimaryFx(
  vm,
  ilsAmount,
  liveRates = null,
  roomRow = null
) {
  const root = vm && typeof vm === "object" ? vm : {};
  const ils = toPositiveFinite(ilsAmount);

  const stampedAmount =
    toPositiveFinite(roomRow?.price_primary) ??
    toPositiveFinite(root.final_price_primary);
  const stampedCur =
    normCur(roomRow?.currency) ||
    normCur(root.primary_currency) ||
    normCur(root.supplier_currency);

  if (stampedAmount != null && stampedCur && stampedCur !== "ILS" && stampedCur !== "NIS") {
    const rateStamped = toPositiveFinite(root.fx_rate_to_ils);
    const liveRate = rateFromLive(liveRates, stampedCur);
    const rate = rateStamped ?? liveRate;
    // Snapshot primary is the pre-fee supplier amount ($4,081 next to ₪14,080).
    if (ils != null && rate != null && rate > 0) {
      const impliedIls = stampedAmount * rate;
      if (Math.abs(impliedIls - ils) / ils > 0.015) {
        const amount = ils / rate;
        return {
          amount,
          currency: stampedCur,
          display: formatOrganizedTourSecondaryCurrencyDisplay(amount, stampedCur),
          source: "displayed_ils_over_stale_primary",
        };
      }
    }
    return {
      amount: stampedAmount,
      currency: stampedCur,
      display: formatOrganizedTourSecondaryCurrencyDisplay(stampedAmount, stampedCur),
      source: "snapshot_primary",
    };
  }

  const rateStamped = toPositiveFinite(root.fx_rate_to_ils);
  const curFromSnap = stampedCur || inferOrganizedTourPublicOfferCurrencyHint(root);
  if (ils != null && rateStamped != null && curFromSnap) {
    const amount = ils / rateStamped;
    return {
      amount,
      currency: curFromSnap,
      display: formatOrganizedTourSecondaryCurrencyDisplay(amount, curFromSnap),
      source: "snapshot_fx_rate",
    };
  }

  const curLive = curFromSnap || inferOrganizedTourPublicOfferCurrencyHint(root) || "USD";
  const liveRate = rateFromLive(liveRates, curLive);
  if (ils != null && liveRate != null) {
    const amount = ils / liveRate;
    return {
      amount,
      currency: curLive,
      display: formatOrganizedTourSecondaryCurrencyDisplay(amount, curLive),
      source: "live_rate",
    };
  }

  return { amount: null, currency: null, display: null, source: "none" };
}

/**
 * פורמט: ₪X,XXX (€Y.YY) / ($Y.YY)
 * @param {string} ilsFormatted — כבר כולל ₪
 * @param {string|null|undefined} primaryDisplay — € / $
 */
export function formatOrganizedTourPublicOfferIlsWithPrimary(ilsFormatted, primaryDisplay) {
  const ils = String(ilsFormatted || "").trim();
  if (!ils) return "—";
  const fx = String(primaryDisplay || "").trim();
  if (!fx) return ils;
  return `${ils} (${fx})`;
}
